export interface CalendarState {
  userid: string;
}

export function buildGoogleCalendarOAuthUrl(
  state: CalendarState,
  isSilent?: boolean,
) {
  const params = {
    client_id:
      process.env.NEXT_PUBLIC_GOOGLE_CALENDAR_OAUTH_CLIENT_ID ||
      "NO GOOGLE CLIENT ID SET",
    redirect_uri: `${window.location.origin}/oauth/google${isSilent ? "?silent=true" : ""}`,
    response_type: "code",
    scope: buildGoogleOAuthScopes().join(" "),
    access_type: "offline",
    include_granted_scopes: "true",
    prompt: "consent",
    state: JSON.stringify(state),
  };

  const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  url.search = new URLSearchParams(params).toString();
  return url.toString();
}

function buildGoogleOAuthScopes() {
  return process.env.NEXT_PUBLIC_REQUEST_ONLY_CALENDAR_SCOPES
    ? ["https://www.googleapis.com/auth/calendar.events.readonly"]
    : [
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/calendar.events.readonly",
      ];
}

export function buildMicrosoftOutlookOAuthUrl(
  state: CalendarState,
  isSilent?: boolean,
) {
  const params = {
    client_id:
      process.env.NEXT_PUBLIC_MICROSOFT_CALENDAR_OAUTH_CLIENT_ID ||
      "NO MICROSOFT CLIENT ID SET",
    redirect_uri: `${window.location.origin}/oauth/microsoft${isSilent ? "?silent=true" : ""}`,
    response_type: "code",
    scope:
      "offline_access openid email https://graph.microsoft.com/Calendars.Read https://graph.microsoft.com/Calendars.Read.Shared",
    state: JSON.stringify(state),
    prompt: "select_account",
    // Default to work/school; an address registered as both can otherwise
    // connect an empty personal mailbox. Hint only -- /common still allows MSA.
    domain_hint: "organizations",
  };

  const url = new URL(
    "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
  );
  url.search = new URLSearchParams(params).toString();

  return url.toString();
}

export function buildZoomOAuthUrl(): string {
  const baseUrl = "https://zoom.us/oauth/authorize";
  const queryParams = {
    response_type: "code",
    redirect_uri: `${window.location.origin}/oauth/zoom`,
    client_id: process.env.NEXT_PUBLIC_ZOOM_CLIENT_ID ?? "NO ZOOM APP SET",
  };
  const queryString = new URLSearchParams(queryParams).toString();
  return `${baseUrl}?${queryString}`;
}

export function buildSalesforceOAuthUrl(loginUrl?: string): string {
  const baseUrl = "https://login.salesforce.com";
  const queryParams = {
    response_type: "code",
    redirect_uri: `${window.location.origin}/oauth/salesforce`,
    client_id:
      process.env.NEXT_PUBLIC_SALESFORCE_CLIENT_ID ?? "NO salesforce APP SET",
    scope:
      "id profile email address phone api web refresh_token offline_access interaction_api",
  };
  const queryString = new URLSearchParams(queryParams).toString();

  return `${baseUrl}?${queryString}`;
}

// Laylah authorize host (bare host, no /api suffix — the /oauth2/auth
// endpoint comes from Laylah's OIDC discovery document). Defaults to the
// production host; override with NEXT_PUBLIC_LAYLAH_OAUTH_BASE_URL
// (e.g. "https://preview.laylah.io") for preview/staging.
const LAYLAH_OAUTH_BASE_URL =
  process.env.NEXT_PUBLIC_LAYLAH_OAUTH_BASE_URL ?? "https://laylah.io";

// Integration scopes requested for the per-user Laylah connection.
// `offline_access` is required so the backend receives a refresh token and can
// keep the connection current without re-prompting the user.
const LAYLAH_OAUTH_SCOPES = [
  "offline_access",
  "integration",
  "contact.search",
  "task.create",
  "task.update",
  "task.delete",
  "comment.create",
  "comment.update",
  "comment.delete",
  "picklist.read",
  "users.read",
];

export function buildLaylahOAuthUrl(state?: CalendarState): string {
  const queryParams: Record<string, string> = {
    client_id:
      process.env.NEXT_PUBLIC_LAYLAH_CLIENT_ID ?? "NO LAYLAH CLIENT ID SET",
    response_type: "code",
    scope: LAYLAH_OAUTH_SCOPES.join(" "),
    redirect_uri: `${window.location.origin}/oauth/laylah`,
  };

  if (state) {
    queryParams.state = JSON.stringify(state);
  }

  const queryString = new URLSearchParams(queryParams).toString();

  return `${LAYLAH_OAUTH_BASE_URL}/oauth2/auth?${queryString}`;
}
